import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory, useLocation } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { useServices } from 'presentation/hooks/use-services'
import {
  AmbulatoryAuthorizationDashboardContext,
  Medication
} from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import DividerNew from 'presentation/shared/components/DividerNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import * as S from './styles'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import CardNew from 'presentation/shared/components/CardNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { validationSchema } from './validationSchema'
import { MedicationCard } from 'presentation/doctor/components/Cards/AmbulatoryMedicationCard'

type LocationProps = {
  isEditing?: boolean
}

const AmbulatoryMedicationLayout = () => {
  const history = useHistory()
  const stateLocation = useLocation()?.state as LocationProps
  const ambulatoryService = useServices().ambulatory
  const { state, dispatch } = useContext<any>(
    AmbulatoryAuthorizationDashboardContext
  )
  const { patient, medications, health_insurance_info } = state

  const [medicationSearchTerm, setMedicationSearchTerm] = useState('')
  const [medicationsList, setMedicationsList] = useState<any>([])
  const [selectedMedication, setSelectedMedication] = useState<any>(null)
  const [selectedMedicationsList, setSelectedMedicationsList] = useState<
    Medication[]
  >([])

  if (!patient?.cpf) {
    history.push('/pedidos-autorizacao-ambulatorial/solicitacao')
  }

  const formik = useFormik({
    initialValues: {
      quantity: '',
      administration: '',
      dosage: ''
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      addMedication(values)
    }
  })

  function selectMedication(medication: any) {
    setMedicationSearchTerm(medication.name)
    setSelectedMedication(medication)
  }

  const clearSelection = () => {
    setMedicationSearchTerm('')
    setSelectedMedication(null)
  }

  async function getMedications(searchTerm: string) {
    setMedicationsList([])
    try {
      const response = await ambulatoryService.getAmbulatoryMedications({
        search: searchTerm,
        healthInsuranceCode: health_insurance_info.health_insurance_code,
        pageSize: 5,
        pageNumber: 1
      })

      const mappedMedicationList = response.data.map((item) => {
        return {
          label: item.name,
          value: item.code,
          ...item
        }
      })

      setMedicationsList(mappedMedicationList)
    } catch (error: any) {
      toast.error(error?.message)
    }
  }

  function addMedication(values: any) {
    const isDuplicate = selectedMedicationsList.some(
      (medication: Medication) =>
        Number(medication.code) === Number(selectedMedication.code)
    )

    if (isDuplicate) {
      toast.error('Este medicamento já foi adicionado.')
      return
    }

    const list = [...selectedMedicationsList]

    list.push({
      name: selectedMedication.name,
      code: Number(selectedMedication.code),
      quantity: values.quantity,
      routeOfAdministration: values.administration,
      dosage: values.dosage,
      isMain: list.length === 0
    })

    setSelectedMedication(undefined)
    setMedicationSearchTerm('')
    setSelectedMedicationsList(list)
    formik.resetForm()
  }

  function removeMedication(code: number) {
    const indexToRemove = selectedMedicationsList.findIndex(
      (medication: Medication) => medication.code === code
    )

    const updatedList = selectedMedicationsList.filter(
      (medication: Medication) => medication.code !== code
    )

    if (selectedMedicationsList[indexToRemove]?.isMain) {
      if (updatedList[indexToRemove]) {
        updatedList[indexToRemove].isMain = true
      } else if (updatedList.length > 0) {
        updatedList[0].isMain = true
      }
    }

    setSelectedMedicationsList(updatedList)
  }

  const handleInputQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    formik.setFieldValue('quantity', value)
  }

  const handleInputQuantityBlur = (e: ChangeEvent<HTMLInputElement>) => {
    let value = Number(e.target.value)

    if (value <= 0) {
      value = 1
    }

    formik.setFieldValue('quantity', value)
  }

  const handleQuantityChange = (
    code: number,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const list = [...selectedMedicationsList]
    const newQuantity = e.target.value

    const newList = list.map((medication: Medication) =>
      medication.code === code
        ? { ...medication, quantity: newQuantity }
        : medication
    )
    setSelectedMedicationsList(newList)
  }

  const handleQuantityBlur = (
    code: number,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const list = [...selectedMedicationsList]
    let newQuantity = Number(e.target.value)

    if (newQuantity <= 0) {
      newQuantity = 1
    }

    const newList = list.map((medication: Medication) =>
      medication.code === code
        ? { ...medication, quantity: newQuantity }
        : medication
    )
    setSelectedMedicationsList(newList)
  }

  function handleSubmit() {
    dispatch({
      type: 'MEDICATIONS',
      payload: selectedMedicationsList
    })

    if (stateLocation?.isEditing) {
      history.push('resumo#med')
    } else {
      history.push('materiais')
    }
  }

  useEffect(() => {
    if (medications?.length) {
      setSelectedMedicationsList(medications)
    }
  }, [])

  return (
    <>
      <HeaderNew
        actualPageOnClick={() =>
          stateLocation?.isEditing ? history.push('resumo') : history.goBack()
        }
        actualPageTitle="Solicitar pedido ambulatorial"
      />

      <ContainerNew
        title="Medicação"
        titleColor="neutral800"
        titleSize="large"
        titleWeight="bold"
        subtitle="Inclua as medicações que serão utilizadas, suas quantidades e/ou
            dosagens."
        primaryButton={
          <ButtonNew fullWidth size="large" onClick={handleSubmit}>
            {stateLocation?.isEditing ? 'Salvar' : 'Próximo'}
          </ButtonNew>
        }
      >
        <S.Wrapper>
          <S.FormWrapper onSubmit={formik.handleSubmit}>
            <AutoCompleteNew
              name="medication"
              placeholder="Digite o nome da medicação"
              onInputChange={setMedicationSearchTerm}
              returnFullSuggestion
              labelOverlapping
              label="Medicação "
              labelSize="xxsmall"
              iconPosition="insideLeft"
              icon={<SearchIcon />}
              minCharactersSearch={3}
              suggestions={medicationsList || []}
              onSuggestionClick={(medication) => selectMedication(medication)}
              value={medicationSearchTerm}
              onType={getMedications}
              debounceDelay={800}
              disabled={!!selectedMedication}
              clear={clearSelection}
              required
              requiredColor="neutral800"
            />
            <TextFieldNew
              labelOverlapping
              label="Quantidade "
              name="quantity"
              placeholder="Digite ou selecione"
              labelSize="xxsmall"
              type="number"
              value={formik.values.quantity}
              onChange={(e) => handleInputQuantityChange(e)}
              onBlur={(e) => handleInputQuantityBlur(e)}
              required
              requiredColor="neutral800"
              min={1}
              error={formik.touched.quantity && formik.errors.quantity}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <TextFieldNew
              labelOverlapping
              label="Via de administração "
              placeholder="Digite"
              labelSize="xxsmall"
              name="administration"
              value={formik.values.administration}
              onChange={formik.handleChange}
              error={
                formik.touched.administration && formik.errors.administration
              }
            />
            <TextFieldNew
              labelOverlapping
              label="Dosagem "
              placeholder="Digite"
              labelSize="xxsmall"
              name="dosage"
              value={formik.values.dosage}
              onChange={formik.handleChange}
              error={formik.touched.dosage && formik.errors.dosage}
            />
            <ButtonNew
              type="submit"
              size="large"
              outlined
              disabled={!selectedMedication || !formik.isValid}
            >
              Incluir medicação
            </ButtonNew>
          </S.FormWrapper>

          <DividerNew marginTop="24px" marginBottom="24px" />

          <HeadingNew
            as="h6"
            weight="semiBold"
            color="neutral600"
            size="medium"
            style={{ marginBottom: '24px' }}
          >
            Medicações incluídas
          </HeadingNew>

          {selectedMedicationsList?.length ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                paddingBottom: '24px'
              }}
            >
              {selectedMedicationsList.map((medication: Medication) => {
                return (
                  <MedicationCard
                    medication={medication}
                    key={medication?.code}
                    handleQuantityChange={handleQuantityChange}
                    handleQuantityBlur={handleQuantityBlur}
                    removeMedication={removeMedication}
                  />
                )
              })}
            </div>
          ) : (
            <>
              <CardNew
                padding="24px"
                background="neutral50"
                shadow="none"
                border="neutral200"
              >
                <SupportTextNew
                  color="neutral500"
                  size="small"
                  weight="semiBold"
                >
                  Você ainda não incluiu nenhuma medicação
                </SupportTextNew>
              </CardNew>
            </>
          )}
        </S.Wrapper>
      </ContainerNew>
    </>
  )
}

export default AmbulatoryMedicationLayout
